import { Attachment, ProcessJobType } from '@/graphql/graphql';

/** The enum's string values (`'LAS'`, `'DJI_IMAGE'`, …), so the rules below can use plain literals. */
type ProcessTypeKey = `${ProcessJobType}`;

export interface ProcessTypeCandidates {
  /** Every process type the selector enables for this upload; `[0]` is the pre-selected default. */
  types: ProcessJobType[];
  /**
   * True when the upload could reasonably be read as more than one kind of data
   * (a folder of JPGs: DJI, mosaic, or one of the mobile-mapping rigs?), so the
   * pre-selected default is a guess the user must check.
   */
  ambiguous: boolean;
}

/**
 * Derives the process types offered for an upload batch from its file names.
 *
 * Types are split into two kinds:
 * - **interpretations** — what the uploaded data *is* (LAS point cloud, DJI
 *   images, IFC model, …). More than one of these means the default is a guess.
 * - **alternative outputs** — a different product from data whose kind is not
 *   in doubt. LAS/LAZ → GeoTIFF rasterises the same point cloud (the LAS job
 *   can also emit that raster via "Generate raster GeoTIFF"), so a plain
 *   LAS/LAZ upload is not ambiguous even though the selector has two enabled
 *   entries. A real `.tif` in the batch makes GEOTIFF an interpretation.
 *   Likewise DJI / mosaic stay selectable for JPGs that all carry a world
 *   file, without making that upload ambiguous.
 */
export const getProcessTypeCandidates = (files: Pick<Attachment, 'filename'>[]): ProcessTypeCandidates => {
  const fileTypes = files.map((file) => file.filename.split('.').pop()?.toLowerCase());
  const has = (ext: string) => fileTypes.includes(ext);
  const hasJpg = has('jpg') || has('jpeg');
  // A JPEG with a world-file sibling (`ortho.jpg` + `ortho.jgw`) is a
  // georeferenced orthophoto, not a drone/rig photo. Same extensions the
  // server pairs on (`JPEG_WORLD_FILE_EXTS` in preprocess).
  const stemOf = (name: string) => name.toLowerCase().replace(/\.[^.]*$/, '');
  const worldFileStems = new Set(
    files.filter((f) => /\.(jgw|jpgw|jpegw|jpw|wld)$/i.test(f.filename)).map((f) => stemOf(f.filename)),
  );
  const jpgStems = files.filter((f) => /\.jpe?g$/i.test(f.filename)).map((f) => stemOf(f.filename));
  const georeferencedJpgCount = jpgStems.filter((stem) => worldFileStems.has(stem)).length;
  const allJpgsGeoreferenced = jpgStems.length > 0 && georeferencedJpgCount === jpgStems.length;

  const interpretations: ProcessTypeKey[] = [];
  const alternativeOutputs: ProcessTypeKey[] = [];
  const add = (...types: ProcessTypeKey[]) => interpretations.push(...types);

  if (has('jp2')) add('JP2');
  if (georeferencedJpgCount > 0) add('JPG');
  if (has('dwg')) add('DWG');
  if (has('dxf')) add('DXF');
  if (has('shp')) add('SHP');
  if (has('kml') || has('kmz')) add('KML');
  if (has('gpkg')) add('GPKG');
  if (has('e57')) add('E57');
  if (has('las') || has('laz')) {
    add('LAS');
    alternativeOutputs.push('GEOTIFF');
  }
  // GreenValley LiDAR360MLS pano export: `pose.txt` + `imglist.ini` next to
  // the JPGs. Added ahead of PTS so the `.txt` doesn't make PTS the default.
  const lowerNames = files.map((f) => f.filename.toLowerCase());
  const hasGviPose =
    lowerNames.some((n) => /(^|_)imglist\.ini$/.test(n)) ||
    lowerNames.some((n) => /(^|_)pose\.txt$/.test(n) && !n.includes('imgpose'));
  if (hasGviPose && hasJpg) add('GVI_IMAGE');
  if (has('pts') || has('xyz') || has('txt')) add('PTS');
  if (has('cal') && has('lst') && has('iprj')) add('IPRJ');
  if (has('geojson') || has('json')) add('GEOJSON');
  if (has('ifc')) add('IFC_FRAGMENT', 'IFC');
  if (has('xml')) add('LANDXML');
  if (has('lcc')) add('XGRIDS');
  if (['spz', 'ply', 'splat', 'ksplat', 'sog'].some(has)) add('SPLAT');
  if (has('tif') || has('tiff')) add('GEOTIFF');
  if (has('rcs')) add('RCS');
  if (has('csv') && hasJpg) add('FJD_IMAGE', 'TRIMBLE_IMAGE', 'TOPCON_IMAGE', 'OMNISLAM_IMAGE', 'LEICA_IMAGE');
  const hasImgPoseTxt = files.some(
    (f) => f.filename.toLowerCase().includes('imgpose') && f.filename.toLowerCase().endsWith('.txt'),
  );
  if (hasImgPoseTxt && hasJpg) add('SOUTH_IMAGE');
  // Every JPG has a world file: still offer DJI / mosaic, but as alternatives
  // to a JPG ortho whose kind is not in doubt.
  if (hasJpg) (allJpgsGeoreferenced ? alternativeOutputs : interpretations).push('DJI_IMAGE', 'MOSAIC_IMAGE');
  if ((has('txt') || has('posc')) && hasJpg) add('CHC_IMAGE');

  const distinctInterpretations = new Set(interpretations);
  return {
    types: [...new Set([...interpretations, ...alternativeOutputs])] as ProcessJobType[],
    ambiguous: distinctInterpretations.size > 1,
  };
};
